import React, { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Share2, Copy, Check, MessageCircle, Mail } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const ShareTripButton = ({ rideId }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [copied, setCopied] = useState(false);

  const generateLink = async () => {
    setLoading(true);
    try {
      const api = (await import('../utils/api')).default;
      const { data } = await api.post(`/rides/${rideId}/share`);
      const fullUrl = `${window.location.origin}${data.shareUrl}`;
      setShareData({ ...data, fullUrl });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to generate share link', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = async () => {
    setOpen(true);
    if (!shareData) await generateLink();
  };

  const handleCopy = async () => {
    if (!shareData?.fullUrl) return;
    await navigator.clipboard.writeText(shareData.fullUrl);
    setCopied(true);
    toast({ title: 'Link copied' });
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShareNative = async () => {
    if (navigator.share && shareData?.fullUrl) {
      try {
        await navigator.share({
          title: 'Follow my Tekstar ride',
          text: 'Track my live ride on Tekstar:',
          url: shareData.fullUrl,
        });
      } catch {
        // user cancelled
      }
    } else {
      handleCopy();
    }
  };

  const message = shareData ? encodeURIComponent(`Follow my Tekstar ride live: ${shareData.fullUrl}`) : '';

  return (
    <>
      <Button
        variant="outline"
        className="w-full border-pink-300 text-pink-600 hover:bg-pink-50"
        onClick={handleOpen}
        data-testid="share-trip-btn"
      >
        <Share2 className="h-4 w-4 mr-2" />
        Share Trip with Family
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="share-trip-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="h-5 w-5 text-pink-600" />
              Share live trip
            </DialogTitle>
          </DialogHeader>

          {loading && <p className="text-sm text-gray-600 py-4">Generating link...</p>}

          {shareData && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">Anyone with this link can follow your ride live until it expires.</p>
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <input
                  readOnly
                  value={shareData.fullUrl}
                  className="flex-1 bg-transparent text-xs outline-none"
                  data-testid="share-trip-url"
                />
                <Button size="sm" variant="ghost" onClick={handleCopy} data-testid="share-copy-btn">
                  {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>

              <p className="text-xs text-gray-500">Expires: {new Date(shareData.expiresAt).toLocaleString()}</p>

              <div className="grid grid-cols-3 gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(`https://wa.me/?text=${message}`, '_blank')}
                  data-testid="share-whatsapp-btn"
                >
                  <MessageCircle className="h-4 w-4 mr-1" />
                  WhatsApp
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(`sms:?body=${message}`, '_blank')}
                  data-testid="share-sms-btn"
                >
                  <MessageCircle className="h-4 w-4 mr-1" />
                  SMS
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(`mailto:?subject=Follow my ride&body=${message}`, '_blank')}
                  data-testid="share-email-btn"
                >
                  <Mail className="h-4 w-4 mr-1" />
                  Email
                </Button>
              </div>

              <Button className="w-full bg-pink-600 hover:bg-pink-700 mt-2" onClick={handleShareNative} data-testid="share-native-btn">
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ShareTripButton;
